/**
 * Preload manager for orchestrating background model downloads
 * V1.4: Smart Preload - Best Fit Model Selection
 *
 * This module handles the preload lifecycle:
 * 1. Check if already cached
 * 2. Detect device capabilities
 * 3. Select best model
 * 4. Download in background
 */
import { selectPreloadModel, getRecommendedStrategy, shouldSkipForConnection } from './selectPreloadModel.js';
import { detectDevice } from '../device/detect.js';
import { getReadyModelIds } from '../cache/cacheUtils.js';
/**
 * Start the preload process.
 * This is non-blocking - returns a promise that resolves when preload completes.
 */
export async function startPreload(input) {
    const { models, modelStore, config, loadModelFn, onSDKProgress } = input;
    // Normalize config
    const preloadConfig = typeof config === 'boolean' ? {} : config;
    // Helper to emit progress events
    const emitProgress = (event) => {
        preloadConfig.onProgress?.(event);
        if (onSDKProgress && event.modelId) {
            onSDKProgress({
                phase: event.phase === 'downloading' ? 'download' : 'init',
                progress: event.progress,
                message: event.message,
                modelId: event.modelId,
            });
        }
    };
    try {
        // Phase 1: Check connection quality (optional)
        if (preloadConfig.respectConnectionQuality && shouldSkipForConnection()) {
            return {
                preloaded: false,
                modelId: null,
                fromCache: false,
                reason: 'Skipped: slow connection or data saver enabled',
            };
        }
        emitProgress({ phase: 'checking', progress: 0, message: 'Checking cache...' });
        // Phase 2: Get cached models
        const cachedModelIds = await getReadyModelIds(modelStore);
        // Phase 3: Detect device
        emitProgress({ phase: 'selecting', progress: 10, message: 'Detecting device...' });
        let device;
        try {
            device = await detectDevice();
        }
        catch (err) {
            // If device detection fails, use conservative defaults
            device = {
                webgpuSupported: false,
                adapterInfo: undefined,
                platform: undefined,
                memory: {
                    deviceMemoryGB: 4,
                },
                inferenceHints: {
                    recommendedMaxModelSizeMB: 500,
                    notes: ['Device detection failed, using conservative defaults'],
                },
                fingerprint: 'unknown:unknown:4:unknown',
                ts: Date.now(),
            };
        }
        // Check minimum memory requirement
        const minMemory = preloadConfig.minMemoryGB ?? 2;
        const deviceMemoryGB = device.memory?.deviceMemoryGB || 4;
        if (deviceMemoryGB < minMemory) {
            return {
                preloaded: false,
                modelId: null,
                fromCache: false,
                reason: `Skipped: device memory (${deviceMemoryGB}GB) below minimum (${minMemory}GB)`,
            };
        }
        // Phase 4: Select model
        emitProgress({ phase: 'selecting', progress: 20, message: 'Selecting best model...' });
        let selectedModelId = null;
        if (preloadConfig.model) {
            // Explicit model specified
            const exists = models.find((m) => m.id === preloadConfig.model);
            if (!exists) {
                throw new Error(`Specified preload model "${preloadConfig.model}" not found in manifest`);
            }
            if (cachedModelIds.includes(preloadConfig.model)) {
                return {
                    preloaded: false,
                    modelId: preloadConfig.model,
                    fromCache: true,
                    reason: `Model "${preloadConfig.model}" already cached`,
                };
            }
            selectedModelId = preloadConfig.model;
        }
        else {
            // Auto-select based on strategy
            const strategy = preloadConfig.strategy || getRecommendedStrategy(device);
            const selection = selectPreloadModel({
                device,
                models,
                cachedModelIds,
                strategy,
            });
            if (selection.skipped) {
                return {
                    preloaded: false,
                    modelId: selection.existingCachedModel?.id || null,
                    fromCache: !!selection.existingCachedModel,
                    reason: selection.reason,
                };
            }
            selectedModelId = selection.selectedModel?.id || null;
        }
        if (!selectedModelId) {
            return {
                preloaded: false,
                modelId: null,
                fromCache: false,
                reason: 'No suitable model found for device',
            };
        }
        // Phase 5: Download model
        emitProgress({
            phase: 'downloading',
            progress: 25,
            message: `Downloading ${selectedModelId}...`,
            modelId: selectedModelId,
        });
        await loadModelFn(selectedModelId);
        emitProgress({
            phase: 'ready',
            progress: 100,
            message: 'Model ready',
            modelId: selectedModelId,
        });
        preloadConfig.onReady?.(selectedModelId);
        return {
            preloaded: true,
            modelId: selectedModelId,
            fromCache: false,
            reason: `Successfully preloaded "${selectedModelId}"`,
        };
    }
    catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        preloadConfig.onError?.(err);
        return {
            preloaded: false,
            modelId: null,
            fromCache: false,
            reason: `Preload failed: ${err.message}`,
        };
    }
}
/**
 * Check if preloading should be attempted.
 * Quick synchronous check before starting async preload.
 */
export function shouldAttemptPreload(config) {
    if (config === undefined || config === false) {
        return false;
    }
    return true;
}
//# sourceMappingURL=preloadManager.js.map